import React, { useState } from "react";
import Modal from "@mui/material/Modal";
import {
    Box, Icon, Typography, DialogContent, DialogTitle, DialogActions, Grid, Checkbox, Divider
} from "@mui/material";
import Button from "@mui/material/Button";
import { alpha } from "@mui/material/styles";


const baseModalSx = {
	position: "relative",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
    width: "min(920px, 92vw)",
	height: "auto",
    maxWidth: "92vw",
	maxHeight: "85%",
	bgcolor: "background.paper",
	border: 1,
	borderColor: "divider",
	boxShadow: 24,
    overflowY: "auto",
    overflowX: "hidden",
    p: 1.5,
	borderRadius: 2,
	display: "flex",
	flexDirection: "column",
} as const;


export type dataComparation = {
    Field?: string;
    ValueBack?: string;
    ValueNew?: string;
}

interface IContextProps {
    ITitle?: string;
    textButtonConfirm?: string;
    showButtonCancel?: boolean;
    showButtonConfirm?: boolean;
    textButtonCancel?: string;
    isOpenModal: boolean;
    messageType?: string;
    IMessage?: React.ReactNode[];
    TitleBack: string;
    TitleNew: string;
    dataComparation: dataComparation[];

    onclickConfirm?: () => void;
    isCloseModal?: () => void;
    contextMessage?: string;
    zwidth?: string;
    showTitleIcon?: boolean;
    enableBackdropClose?: boolean;
    overrideWidthValue?: string;
    overrideHeightValue?: string;
}

export const ModalComparation: React.FC<IContextProps> = ({ ITitle, textButtonConfirm, showButtonConfirm, TitleBack, TitleNew, dataComparation,
    showButtonCancel, isOpenModal, isCloseModal, onclickConfirm, textButtonCancel, enableBackdropClose, overrideWidthValue, overrideHeightValue }) => {

    const handleClose = (event: object, reason: string) => {
        if (reason === 'backdropClick' && isCloseModal) {
            isCloseModal()
        }
    }

    const [checkTerm, setCheckTerm] = useState(false);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCheckTerm(event.target.checked);
    };

    const normalizeValue = (value?: unknown) => (value ?? "").toString().trim();

    const safeComparationRows = Array.isArray(dataComparation)
        ? (dataComparation.filter(Boolean) as dataComparation[])
        : [];


    return (
        <div>

            <Modal
                open={isOpenModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
				onClose={enableBackdropClose ? handleClose : undefined}
            >
                <Box
                    sx={{
                        ...baseModalSx,
                        width: overrideWidthValue ? overrideWidthValue : baseModalSx.width,
                        height: overrideHeightValue ? overrideHeightValue : baseModalSx.height,
                    }}
                >
                    <DialogTitle style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Typography
                            id="modal-modal-title"
                            variant="h5"
                            color={"primary"}
                        >
                            {ITitle}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
						<Box margin={0} display="flex" flexDirection="column" width="100%">
							<Grid container spacing={1.5} alignItems="center" sx={{ mt: 0.5, mb: 0.5 }}>
								<Grid item xs={12} md={3}>
									<Typography variant="caption" color="text.secondary">
										Campo
									</Typography>
								</Grid>
								<Grid item xs={12} md={4}>
									<Typography variant="caption" color="text.secondary">
										{TitleBack}
									</Typography>
								</Grid>
								<Grid item xs={12} md={5}>
									<Typography variant="caption" color="text.secondary">
										{TitleNew}
									</Typography>
								</Grid>
							</Grid>

							<Box>
								{safeComparationRows.map((row, index) => {
									const back = normalizeValue(row.ValueBack);
									const next = normalizeValue(row.ValueNew);
									const isChanged = back !== next;

									return (
										<Box key={`${row.Field ?? "field"}-${index}`} sx={{ py: 0.75 }}>
											<Grid container spacing={1.5} alignItems="flex-start">
												<Grid item xs={12} md={3}>
													<Typography variant="body2" color="text.primary" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
														{row.Field}
													</Typography>
												</Grid>

												<Grid item xs={12} md={4}>
														<Box
															sx={(theme) => ({
																backgroundColor: theme.palette.action.hover,
																borderRadius: 1,
																px: 1,
																py: 0.75,
																minWidth: 0,
														})}
														>
															<Typography
																variant="body2"
																color="text.primary"
																sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "anywhere" }}
															>
																{back}
															</Typography>
														</Box>
												</Grid>

												<Grid item xs={12} md={5}>
													<Box display="flex" alignItems="flex-start" gap={1}>
														<Box
															sx={(theme) => ({
																flex: 1,
																minWidth: 0,
																borderRadius: 1,
																	px: 1,
																	py: 0.75,
																		backgroundColor: isChanged ? alpha(theme.palette.primary.main, 0.08) : theme.palette.action.hover,
														})}
														>
															<Typography
																variant="body2"
																		color={isChanged ? "primary" : "text.primary"}
																sx={{
																	fontWeight: isChanged ? 700 : 400,
																	whiteSpace: "pre-wrap",
																	wordBreak: "break-word",
																	overflowWrap: "anywhere",
																}}
															>
																{next}
															</Typography>
														</Box>

														<Box
															sx={(theme) => ({
																width: 32,
																height: 32,
																borderRadius: "50%",
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																flexShrink: 0,
																backgroundColor: isChanged
																		? alpha(theme.palette.primary.main, 0.12)
																	: theme.palette.action.hover,
														})}
														>
															{isChanged ? (
																	<Icon sx={{ color: "primary.main" }}>check</Icon>
															) : (
																<Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700, lineHeight: 1 }}>
																	=
																</Typography>
															)}
														</Box>
													</Box>
												</Grid>
											</Grid>
										</Box>
									);
								})}
							</Box>

							<Box display="flex" justifyContent="center" alignItems="center" gap={1} sx={{ mt: 1.5 }}>
								<Checkbox
									checked={checkTerm}
									onChange={handleChange}
									inputProps={{ "aria-label": "controlled" }}
									sx={{ "& .MuiSvgIcon-root": { fontSize: 26 } }}
								/>
								<Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
									Confirmo que estou ciente que os dados serão modificados de acordo com o demonstrativo acima
								</Typography>
							</Box>
						</Box>
                    </DialogContent>

					<Divider sx={{ mt: 1.5 }} />

                    <DialogActions style={{ display: 'flex', justifyContent: 'center' }}          >
                        {showButtonCancel && (
                            <Button
                                onClick={isCloseModal}
                                color="primary"
                                disableElevation
                                variant={"outlined"}
                                startIcon={< Icon > closeIcon</Icon >}
                            >
                                {textButtonCancel}
                            </Button>
                        )}
                        {showButtonConfirm && (
                            <Button
                                color="primary"
                                disableElevation
                                variant={"contained"}
                                disabled={!checkTerm}
                                startIcon={< Icon > checkIcon</Icon >}
                                onClick={onclickConfirm}
                            >
                                {textButtonConfirm}
                            </Button>
                        )}

                    </DialogActions>
                </Box>
            </Modal >
        </div >
    );
}